import type { AutomationSchedule } from '@/integration/types.js';

/**
 * Zero-dependency humanizer for standard 5-field cron expressions
 * (`min hour day-of-month month day-of-week`). Covers the common presets the
 * schedule picker emits — every-minute, every-N-minutes, hourly, every-N-hours, daily, weekly —
 * plus the shapes a custom (interpreted) schedule produces: weekday lists and ranges, and
 * day-of-month lists. Anything it can't confidently read falls back to the user's own
 * `scheduleText` when there is one, and to `${cron} (${tz})` otherwise, so the raw expression
 * is never hidden.
 *
 * Intentionally NOT cronstrue: the task forbids new dependencies, and we only
 * need to render what our own picker and the schedule interpreter produce.
 */

const WEEKDAYS: Record<number, string> = {
  0: 'Sundays',
  1: 'Mondays',
  2: 'Tuesdays',
  3: 'Wednesdays',
  4: 'Thursdays',
  5: 'Fridays',
  6: 'Saturdays',
};

/** A planned schedule whose words this caller does not have. */
const PLANNED_WITHOUT_WORDS = 'Custom schedule';

function pad2(n: number): string {
  return String(n).padStart(2, '0');
}

/** Parse a single cron field into an integer, or null if not a plain number. */
function num(field: string | undefined): number | null {
  if (field === undefined || !/^\d+$/.test(field)) return null;
  const n = Number(field);
  return Number.isInteger(n) ? n : null;
}

/** Expand valid numbers and `a-b` ranges in field order, checking the field bounds before expansion. */
function numberList(
  field: string | undefined,
  min: number,
  max: number,
): number[] | null {
  if (field === undefined) return null;
  const values: number[] = [];
  for (const token of field.split(',')) {
    const range = /^(\d+)-(\d+)$/.exec(token);
    if (range) {
      const from = Number(range[1]);
      const to = Number(range[2]);
      if (from < min || to > max || from > to) return null;
      for (let day = from; day <= to; day += 1) values.push(day);
      continue;
    }
    const single = num(token);
    if (single === null || single < min || single > max) return null;
    values.push(single);
  }
  return values.length > 0 ? values : null;
}

/** "A", "A and B", "A, B and C". */
function joinNames(names: readonly string[]): string {
  if (names.length <= 1) return names.join('');
  return `${names.slice(0, -1).join(', ')} and ${names[names.length - 1]}`;
}

/** 1 → "1st", 2 → "2nd", 3 → "3rd", 11 → "11th", 21 → "21st", 31 → "31st". */
function ordinal(day: number): string {
  const tens = day % 100;
  if (tens >= 11 && tens <= 13) return `${day}th`;
  const suffix = { 1: 'st', 2: 'nd', 3: 'rd' }[day % 10] ?? 'th';
  return `${day}${suffix}`;
}

/** "Weekdays" for Monday–Friday in any spelling; otherwise the weekday names in field order. */
function weekdayNames(dowRaw: string | undefined): string | null {
  const days = numberList(dowRaw, 0, 7);
  if (days === null) return null;
  const normalized = days.map((day) => (day === 7 ? 0 : day));
  if (normalized.some((day) => day < 0 || day > 6)) return null;
  const sorted = [...new Set(normalized)].sort((left, right) => left - right);
  if (sorted.join(',') === '1,2,3,4,5') return 'Weekdays';
  return joinNames([...new Set(normalized)].map((day) => WEEKDAYS[day] ?? ''));
}

/** "the 1st", "the 1st and 15th" for a day-of-month list. */
function monthDayNames(domRaw: string | undefined): string | null {
  const days = numberList(domRaw, 1, 31);
  if (days === null || days.some((day) => day < 1 || day > 31)) return null;
  return `the ${joinNames(days.map(ordinal))}`;
}

/** A star-slash-N step field → N (N ≥ 1); null for any other field shape. */
function step(field: string | undefined): number | null {
  const match = /^\*\/(\d+)$/.exec(field ?? '');
  if (!match) return null;
  const value = Number(match[1]);
  return value >= 1 ? value : null;
}

/** The every-N-minutes / hourly family: a plain, star or step minute with every other field a star. */
function humanizeEveryHour(minRaw: string | undefined): string | null {
  if (minRaw === '*') return 'Every minute';
  const minuteStep = step(minRaw);
  if (minuteStep !== null) {
    return minuteStep === 1 ? 'Every minute' : `Every ${minuteStep} minutes`;
  }
  const minute = num(minRaw);
  if (minute === null || minute > 59) return null;
  return minute === 0 ? 'Hourly' : `Hourly (at :${pad2(minute)})`;
}

/** Hour steps dividing the day keep interval wording; a 24-hour step runs once daily. */
function humanizeHourStep(
  minRaw: string | undefined,
  hourRaw: string | undefined,
): string | null {
  const hourStep = step(hourRaw);
  const minute = num(minRaw);
  if (
    hourStep === null ||
    minute === null ||
    minute > 59 ||
    hourStep > 24 ||
    24 % hourStep !== 0
  )
    return null;
  if (hourStep === 24) return `Daily at 00:${pad2(minute)}`;
  const every = hourStep === 1 ? 'Hourly' : `Every ${hourStep} hours`;
  return minute === 0 ? every : `${every} (at :${pad2(minute)})`;
}

/**
 * Render a schedule in plain English. `scheduleText` is the user's own wording of a custom
 * schedule; it stands in only when the cron itself cannot be read.
 *
 * On-demand automations carry no cron, so there is nothing to humanize. That is keyed on the cron
 * field being absent (nullish) rather than on `type === 'manual'`, so a manual schedule that
 * somehow carries a cron (type/cron drift) still gets parsed instead of silently hidden. An
 * empty or whitespace-only cron is invalid-but-present data, distinct from "no schedule": the
 * generic `${cron} (${tz})` fallback would render it as an uninformative " (UTC)", hiding that
 * the data is bad, so it is called out explicitly instead.
 *
 * A planned schedule has no cron either, but it is not on demand: its words are the schedule.
 */
export function cronHumanize(
  schedule: AutomationSchedule,
  scheduleText?: string | null,
): string {
  const { cron, tz } = schedule;
  if (schedule.type === 'planned')
    return scheduleText?.trim() || PLANNED_WITHOUT_WORDS;
  if (cron === undefined || cron === null) return 'On demand';
  const trimmed = cron.trim();
  if (trimmed === '') return `Invalid schedule: no cron expression (${tz})`;
  const readable = humanizeFields(trimmed.split(/\s+/));
  if (readable !== null) return readable;
  const ownWords = scheduleText?.trim();
  return ownWords ? ownWords : `${cron} (${tz})`;
}

/** The readable sentence for a 5-field expression, or null when no known shape matches. */
function humanizeFields(fields: readonly string[]): string | null {
  if (fields.length !== 5) return null;
  const [minRaw, hourRaw, domRaw, monRaw, dowRaw] = fields;
  if (monRaw !== '*') return null;
  if (hourRaw === '*' && domRaw === '*' && dowRaw === '*') {
    return humanizeEveryHour(minRaw);
  }
  if (domRaw === '*' && dowRaw === '*' && step(hourRaw) !== null) {
    return humanizeHourStep(minRaw, hourRaw);
  }
  const minute = num(minRaw);
  const hour = num(hourRaw);
  if (minute === null || minute > 59 || hour === null || hour > 23) return null;
  const at = `${pad2(hour)}:${pad2(minute)}`;
  if (domRaw === '*' && dowRaw === '*') return `Daily at ${at}`;
  if (domRaw === '*') {
    const days = weekdayNames(dowRaw);
    return days === null ? null : `${days} at ${at}`;
  }
  if (dowRaw === '*') {
    const days = monthDayNames(domRaw);
    return days === null ? null : `Monthly on ${days} at ${at}`;
  }
  return null;
}
